import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Loader2, X, Pencil, Check, Clock, GripVertical, ChevronDown, ChevronRight, FileText, FileSpreadsheet } from "lucide-react";
import { api, downloadFile } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { useCompactMoney } from "@/lib/useCompactMoney";
import { toast } from "sonner";
import { ReportShell } from "./atoms";

const ASSET_CATEGORIES = ["Current Asset", "Fixed Asset", "Intangible Asset", "Other Asset"];
const LIABILITY_CATEGORIES = ["Current Liability", "Long-Term Liability", "Deferred Revenue", "Other Liability"];
const COLLAPSE_KEY = "ll_bs_collapsed_sections";

// Classic Vertex42-style Balance Sheet: Assets (Current / Fixed / Other),
// Liabilities and Owner's Equity (Current / Long-Term / Equity), plus a
// Common Financial Ratios footer. Every section is editable in place —
// users can add, rename, and remove manual assets & liabilities.
export default function BalanceSheetView({ bs, onExport, onShare, onBsReload, compareMode, onCompareModeChange }) {
  const { money } = useCompactMoney();
  const [manualAssets, setManualAssets] = useState([]);
  const [manualLiabs, setManualLiabs] = useState([]);
  const [schedules, setSchedules] = useState([]);

  // Per-section collapsed state, persisted so a user who hides "Other Assets"
  // stays hidden on the next visit.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const toggleSection = (key) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  const SECTION_KEYS = ["ca", "fa", "oa", "cl", "ll", "eq"];
  const collapseAll = () => {
    const next = SECTION_KEYS.reduce((m, k) => ({ ...m, [k]: true }), {});
    setCollapsed(next);
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };
  const expandAll = () => {
    setCollapsed({});
    try { localStorage.setItem(COLLAPSE_KEY, "{}"); } catch { /* ignore */ }
  };
  const allCollapsed = SECTION_KEYS.every((k) => collapsed[k]);

  const loadMeta = async () => {
    try {
      const [a, l, s] = await Promise.all([
        api.get("/manual-assets"),
        api.get("/manual-liabilities"),
        api.get("/depreciation"),
      ]);
      setManualAssets(a.data || []);
      setManualLiabs(l.data || []);
      setSchedules(s.data || []);
    } catch { /* non-fatal — editable rows just won't have the manual metadata */ }
  };

  useEffect(() => { loadMeta(); /* eslint-disable-next-line */ }, []);

  const reloadAll = async () => {
    await Promise.all([loadMeta(), onBsReload && onBsReload()]);
  };

  const reorderAssets = async (ids) => {
    try {
      await api.post("/manual-assets/reorder", { ids });
      await reloadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reorder failed");
    }
  };
  const reorderLiabs = async (ids) => {
    try {
      await api.post("/manual-liabilities/reorder", { ids });
      await reloadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reorder failed");
    }
  };

  // Join the computed BS rows (which have id for manual ones) with the raw
  // manual records so edits can post back by id. Cash rows are read-only.
  const assetMap = useMemo(() => {
    const m = {};
    manualAssets.forEach((a) => { m[a.id] = a; });
    return m;
  }, [manualAssets]);
  const liabMap = useMemo(() => {
    const m = {};
    manualLiabs.forEach((l) => { m[l.id] = l; });
    return m;
  }, [manualLiabs]);
  const scheduleByAsset = useMemo(() => {
    const m = {};
    schedules.forEach((s) => { m[s.asset_id] = s; });
    return m;
  }, [schedules]);

  const ratios = useMemo(() => {
    const ta = bs.assets.total || 0;
    const tl = bs.liabilities.total || 0;
    const te = bs.equity.total || 0;
    const ca = bs.assets.total_current || 0;
    const cl = bs.liabilities.total_current || 0;
    return {
      debt: ta > 0 ? tl / ta : 0,
      current: cl > 0 ? ca / cl : 0,
      working: ca - cl,
      a2e: te !== 0 ? ta / te : 0,
      d2e: te !== 0 ? tl / te : 0,
    };
  }, [bs]);

  const prior = bs.prior?.totals || null;
  const todayLabel = new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

  return (
    <ReportShell title="Balance Sheet" kind="balance-sheet" onExport={onExport} onShare={onShare} testid="bs-view">
      {/* Compare toggle — adds a prior-period column à la Vertex42's
          "2020 vs 2019" layout. */}
      <div className="flex items-center justify-between flex-wrap gap-2 pb-3">
        {onCompareModeChange ? (
          <div className="flex items-center gap-1" data-testid="bs-compare-toggle">
            <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mr-2">Compare</span>
            {[
              { v: null, label: "None" },
              { v: "prior_period", label: "Prior period" },
              { v: "prior_year", label: "Prior year" },
            ].map((opt) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => onCompareModeChange(opt.v)}
                aria-pressed={compareMode === opt.v}
                data-testid={`bs-compare-${opt.v || "none"}`}
                className={`text-xs px-2.5 py-1 border transition-colors ${compareMode === opt.v ? "bg-[#0b5d9e] text-white border-[#0b5d9e]" : "hover:bg-[var(--ll-mint)]"}`}
                style={{ borderColor: compareMode === opt.v ? "#0b5d9e" : "var(--ll-border)" }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        ) : <span />}
        <button
          type="button"
          onClick={allCollapsed ? expandAll : collapseAll}
          data-testid="bs-collapse-all"
          className="text-xs px-2.5 py-1 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1"
          style={{ borderColor: "var(--ll-border)" }}
          title={allCollapsed ? "Expand every section" : "Collapse every section for print / screenshot"}
        >
          {allCollapsed ? <><ChevronDown size={12} /> Expand all</> : <><ChevronRight size={12} /> Collapse all</>}
        </button>
      </div>

      {/* Period header — mirrors Vertex42's "2020 vs 2019" column layout */}
      {prior && (
        <div className="flex items-center justify-end gap-4 px-4 pb-2 text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]" data-testid="bs-period-header">
          <span className="w-28 text-right">{bs.prior.label}</span>
          <span className="w-32 text-right font-semibold text-[var(--ll-ink)]">As of {todayLabel}</span>
        </div>
      )}

      {/* Assets band */}
      <SectionBand label="Assets" />

      <SubSection label="Current Assets" onReorder={reorderAssets} collapsed={collapsed.ca} onToggle={() => toggleSection("ca")}>
        {bs.assets.current.map((r) => (
          <StaticOrEditable
            key={r.id || r.name}
            row={r}
            editable={!!r.manual}
            raw={r.manual ? assetMap[r.id] : null}
            kind="asset"
            onReload={reloadAll}
          />
        ))}
      </SubSection>
      <TotalRow label="Total current assets" value={bs.assets.total_current} prior={prior?.total_current_assets} />

      <SubSection
        label="Fixed (Long-Term) Assets"
        onReorder={reorderAssets}
        collapsed={collapsed.fa}
        onToggle={() => toggleSection("fa")}
        add={{ kind: "asset", category: "Fixed Asset", onReload: reloadAll, placeholder: "Property, plant, or equipment" }}
      >
        {bs.assets.fixed.map((r) => (
          <StaticOrEditable
            key={r.id}
            row={r}
            editable
            raw={assetMap[r.id]}
            kind="asset"
            onReload={reloadAll}
            schedule={scheduleByAsset[r.id]}
          />
        ))}
      </SubSection>
      <TotalRow label="Total fixed assets" value={bs.assets.total_fixed} prior={prior?.total_fixed_assets} />

      <SubSection
        label="Other Assets"
        onReorder={reorderAssets}
        collapsed={collapsed.oa}
        onToggle={() => toggleSection("oa")}
        add={{ kind: "asset", category: "Other Asset", onReload: reloadAll, placeholder: "Deferred income tax, intangibles, other" }}
      >
        {bs.assets.other.map((r) => (
          <StaticOrEditable
            key={r.id}
            row={r}
            editable
            raw={assetMap[r.id]}
            kind="asset"
            onReload={reloadAll}
          />
        ))}
      </SubSection>
      <TotalRow label="Total Other Assets" value={bs.assets.total_other} prior={prior?.total_other_assets} />

      <TotalRow label="Total Assets" value={bs.assets.total} prior={prior?.total_assets} bold compactMoney={money} testid="bs-total-assets" />

      {/* Liabilities band */}
      <div className="mt-6" />
      <SectionBand label="Liabilities and Owner's Equity" />

      <SubSection
        label="Current Liabilities"
        onReorder={reorderLiabs}
        collapsed={collapsed.cl}
        onToggle={() => toggleSection("cl")}
        add={{ kind: "liability", category: "Current Liability", onReload: reloadAll, placeholder: "A/P, taxes payable, accrued expenses" }}
      >
        {bs.liabilities.current.map((r) => (
          <StaticOrEditable
            key={r.id || r.name}
            row={r}
            editable={!!r.manual}
            raw={r.manual ? liabMap[r.id] : null}
            kind="liability"
            onReload={reloadAll}
          />
        ))}
      </SubSection>
      <TotalRow label="Total current liabilities" value={bs.liabilities.total_current} prior={prior?.total_current_liabilities} />

      <SubSection
        label="Long-Term Liabilities"
        onReorder={reorderLiabs}
        collapsed={collapsed.ll}
        onToggle={() => toggleSection("ll")}
        add={{ kind: "liability", category: "Long-Term Liability", onReload: reloadAll, placeholder: "Long-term debt, deferred tax, other" }}
      >
        {bs.liabilities.long_term.map((r) => (
          <StaticOrEditable
            key={r.id}
            row={r}
            editable
            raw={liabMap[r.id]}
            kind="liability"
            onReload={reloadAll}
          />
        ))}
      </SubSection>
      <TotalRow label="Total long-term liabilities" value={bs.liabilities.total_long_term} prior={prior?.total_long_term_liabilities} />

      <SubSection label="Owner's Equity" collapsed={collapsed.eq} onToggle={() => toggleSection("eq")}>
        {bs.equity.items.map((r) => (
          <StaticOrEditable key={r.name} row={r} editable={false} kind="equity" />
        ))}
      </SubSection>
      <TotalRow label="Total owner's equity" value={bs.equity.total} prior={prior?.total_equity} />

      <TotalRow label="Total Liabilities and Owner's Equity" value={bs.total_liabilities_and_equity} prior={prior?.total_liabilities_and_equity} bold compactMoney={money} testid="bs-total-le" />

      {/* Ratios band */}
      <div className="mt-6" />
      <SectionBand label="Common Financial Ratios" />
      <div className="divide-y border-x border-b" style={{ borderColor: "var(--ll-border)" }}>
        <RatioRow label="Debt Ratio" formula="Total Liabilities / Total Assets" value={ratios.debt.toFixed(2)} />
        <RatioRow label="Current Ratio" formula="Current Assets / Current Liabilities" value={ratios.current.toFixed(2)} />
        <RatioRow label="Working Capital" formula="Current Assets - Current Liabilities" value={fmtMoney(ratios.working)} />
        <RatioRow label="Assets-to-Equity Ratio" formula="Total Assets / Owner's Equity" value={ratios.a2e.toFixed(2)} />
        <RatioRow label="Debt-to-Equity Ratio" formula="Total Liabilities / Owner's Equity" value={ratios.d2e.toFixed(2)} />
      </div>
    </ReportShell>
  );
}

/* -------- Visual atoms -------- */

function SectionBand({ label }) {
  return (
    <div
      className="font-display font-bold text-base px-4 py-2 text-white"
      style={{ background: "#0b5d9e" }}
    >
      {label}
    </div>
  );
}

function SubSection({ label, children, add, onReorder, collapsed = false, onToggle }) {
  const [adding, setAdding] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const kids = Array.isArray(children) ? children.filter(Boolean) : (children ? [children] : []);
  const hasChildren = kids.length > 0;

  // Wire drag handlers onto each child row so firms can reorder a section
  // to match a client's chart-of-accounts. We only reorder editable
  // (manual) rows — read-only ones (cash lines, loan placeholder) stay put.
  const draggableKids = onReorder
    ? kids.map((child, i) => {
        const id = child?.props?.row?.id;
        const canDrag = !!id && !!child?.props?.editable;
        if (!canDrag) return child;
        return (
          <div
            key={id}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
            onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
            onDrop={() => {
              if (dragIdx === null || dragIdx === i) return;
              const ids = kids.map((c) => c?.props?.row?.id).filter(Boolean);
              const moved = ids.splice(dragIdx, 1)[0];
              ids.splice(i, 0, moved);
              onReorder(ids);
              setDragIdx(null); setOverIdx(null);
            }}
            className={`${overIdx === i && dragIdx !== null ? "bg-[#DBEAFE]" : ""} ${dragIdx === i ? "opacity-40" : ""}`}
            data-testid={`bs-drag-wrapper-${id}`}
          >
            {child}
          </div>
        );
      })
    : kids;

  return (
    <div className="px-4 border-x border-b" style={{ borderColor: "var(--ll-border)", background: "#F8FAFC" }}>
      <div className="flex items-center justify-between py-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-1.5 font-display italic font-medium text-sm text-[var(--ll-ink)] hover:text-[#0b5d9e] transition-colors"
          data-testid={`bs-collapse-toggle-${label.replace(/\s+/g, "-").toLowerCase()}`}
          aria-expanded={!collapsed}
        >
          {collapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
          <span>{label}</span>
        </button>
        {add && (
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            data-testid={`bs-add-${add.kind}-${add.category.replace(/\s+/g, "-").toLowerCase()}`}
            className="text-[10px] uppercase tracking-widest px-2 py-1 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1"
            style={{ borderColor: "var(--ll-border)" }}
          >
            {adding ? <><X size={10} /> Cancel</> : <><Plus size={10} /> Add</>}
          </button>
        )}
      </div>
      {!collapsed && hasChildren && <div className="pl-6 pr-2 pb-2 bg-white -mx-4 px-4">{draggableKids}</div>}
      {!collapsed && adding && <AddForm {...add} onDone={() => setAdding(false)} />}
    </div>
  );
}

function TotalRow({ label, value, prior, bold = false, compactMoney, testid }) {
  const fmt = compactMoney || fmtMoney;
  return (
    <div
      className={`px-4 py-2 border-x border-b flex items-center justify-between ${bold ? "border-y-2" : ""}`}
      style={{ borderColor: "var(--ll-border)", background: bold ? "#F8FAFC" : "white", borderTopColor: bold ? "#0F1E3C" : "var(--ll-border)" }}
      data-testid={testid}
    >
      <span className={`${bold ? "font-bold text-base" : "text-sm"}`}>{label}</span>
      <div className="flex items-baseline gap-4">
        {prior !== undefined && (
          <span
            className={`font-mono-fin text-[var(--ll-ink-muted)] ${bold ? "text-sm" : "text-xs"} w-28 text-right`}
            data-testid={testid ? `${testid}-prior` : undefined}
          >
            {prior == null || Number.isNaN(prior) ? "—" : fmt(prior)}
          </span>
        )}
        <span className={`font-mono-fin ${bold ? "font-bold text-base" : "text-sm"} w-32 text-right`}>{fmt(value || 0)}</span>
      </div>
    </div>
  );
}

function RatioRow({ label, formula, value }) {
  return (
    <div className="px-4 py-2 flex items-center justify-between gap-3" style={{ background: "white" }}>
      <div className="min-w-0">
        <div className="font-medium text-sm">{label}</div>
        <div className="text-[11px] text-[var(--ll-ink-muted)] truncate">{formula}</div>
      </div>
      <div className="font-mono-fin text-sm">{value}</div>
    </div>
  );
}

/* -------- Editable row -------- */

function StaticOrEditable({ row, editable, raw, kind, onReload, schedule }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(row.name);
  const [amount, setAmount] = useState(row.amount);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const endpoint = kind === "liability" ? "/manual-liabilities" : "/manual-assets";

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt)) { toast.error("Enter a numeric amount"); return; }
    setSaving(true);
    try {
      await api.patch(`${endpoint}/${raw.id}`, { name: name.trim(), amount: amt });
      toast.success("Updated");
      setEditing(false);
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`${endpoint}/${raw.id}`);
      toast.success(`${row.name} removed`);
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="py-1.5 flex items-center gap-2 group" data-testid={`bs-edit-${row.id || row.name}`}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          data-testid={`bs-edit-name-${row.id}`}
          className="flex-1 px-2 py-1 border text-sm"
          style={{ borderColor: "var(--ll-border)" }}
        />
        <input
          type="number"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid={`bs-edit-amount-${row.id}`}
          className="w-32 px-2 py-1 border text-sm font-mono-fin text-right"
          style={{ borderColor: "var(--ll-border)" }}
        />
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid={`bs-edit-save-${row.id}`}
          className="p-1.5 bg-[#0b5d9e] text-white hover:opacity-90 disabled:opacity-50"
          aria-label="Save"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
        </button>
        <button
          type="button"
          onClick={() => { setEditing(false); setName(row.name); setAmount(row.amount); }}
          className="p-1.5 border"
          style={{ borderColor: "var(--ll-border)" }}
          aria-label="Cancel"
        >
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <div className="py-1.5 flex items-center gap-3 group" data-testid={`bs-row-${row.id || row.name}`}>
      {editable && (
        <GripVertical
          size={12}
          className="text-[var(--ll-ink-muted)] opacity-0 group-hover:opacity-100 cursor-grab transition-opacity shrink-0"
          data-testid={`bs-drag-handle-${row.id}`}
        />
      )}
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="text-sm truncate">{row.name}</span>
        {kind === "asset" && row.category === "Fixed Asset" && editable && (
          <DepreciationBadge asset={row} schedule={schedule} onReload={onReload} />
        )}
      </div>
      <span className="font-mono-fin text-sm">{row.amount ? fmtMoney(row.amount) : ""}</span>
      {editable && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label="Edit"
            data-testid={`bs-edit-btn-${row.id}`}
            className="p-1 text-[var(--ll-ink-soft)] hover:text-[#0b5d9e]"
          >
            <Pencil size={12} />
          </button>
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            aria-label="Remove"
            data-testid={`bs-remove-${row.id}`}
            className="p-1 text-[var(--ll-ink-soft)] hover:text-red-600 disabled:opacity-30"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
          </button>
        </div>
      )}
    </div>
  );
}

/* -------- Inline add form -------- */

function AddForm({ kind, category, onReload, onDone, placeholder }) {
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const endpoint = kind === "liability" ? "/manual-liabilities" : "/manual-assets";

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt)) { toast.error("Enter a numeric amount"); return; }
    setSaving(true);
    try {
      await api.post(endpoint, { name: name.trim(), amount: amt, category });
      toast.success("Added");
      setName(""); setAmount("");
      onReload && onReload();
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally {
      setSaving(false);
    }
  };

  const options = kind === "liability" ? LIABILITY_CATEGORIES : ASSET_CATEGORIES;
  void options;

  return (
    <div className="pb-3 flex items-center gap-2" data-testid={`bs-add-form-${kind}`}>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={placeholder || "Name"}
        autoFocus
        data-testid={`bs-add-name-${kind}`}
        className="flex-1 px-2 py-1.5 border text-sm bg-white"
        style={{ borderColor: "var(--ll-border)" }}
      />
      <input
        type="number"
        step="0.01"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        placeholder="0.00"
        data-testid={`bs-add-amount-${kind}`}
        className="w-32 px-2 py-1.5 border text-sm font-mono-fin text-right bg-white"
        style={{ borderColor: "var(--ll-border)" }}
      />
      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid={`bs-add-save-${kind}`}
        className="px-3 py-1.5 bg-[#0b5d9e] text-white text-xs inline-flex items-center gap-1 hover:opacity-90 disabled:opacity-50"
      >
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add
      </button>
    </div>
  );
}

/* -------- Depreciation nudge -------- */

function DepreciationBadge({ asset, schedule, onReload }) {
  const [open, setOpen] = useState(false);
  const [life, setLife] = useState(60);
  const [salvage, setSalvage] = useState("0");
  const [method, setMethod] = useState("straight_line");
  const [saving, setSaving] = useState(false);
  const [booking, setBooking] = useState(false);
  const [catchingUp, setCatchingUp] = useState(false);

  const monthly = useMemo(() => {
    if (!schedule) return 0;
    const cost = asset.amount || 0;
    const salvageVal = schedule.salvage_value || 0;
    const months = schedule.useful_life_months || 1;
    const m = schedule.method || "straight_line";
    if (m === "double_declining") {
      // Approximate first-month amount for display — the exact per-month
      // schedule is on the server; chip shows the headline.
      return (cost * 2) / months;
    }
    if (m === "sum_of_years_digits") {
      const total = (months * (months + 1)) / 2;
      return ((cost - salvageVal) * months) / total;
    }
    return Math.max(cost - salvageVal, 0) / months;
  }, [schedule, asset.amount]);
  const thisMonthIso = new Date().toISOString().slice(0, 7);
  const bookedThisMonth = schedule?.booked_months?.includes(thisMonthIso);
  const totalBooked = (schedule?.booked_months || []).length;
  const totalLife = schedule?.useful_life_months || 0;
  const canCatchUp = schedule && totalBooked < totalLife;

  const setup = async () => {
    setSaving(true);
    try {
      await api.post("/depreciation", {
        asset_id: asset.id,
        useful_life_months: parseInt(life, 10) || 60,
        salvage_value: parseFloat(salvage) || 0,
        method,
      });
      toast.success("Depreciation schedule created");
      setOpen(false);
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Setup failed");
    } finally {
      setSaving(false);
    }
  };

  const catchUp = async () => {
    setCatchingUp(true);
    try {
      const r = await api.post(`/depreciation/${schedule.id}/catch-up`);
      if (r.data.booked_count > 0) {
        toast.success(`Caught up ${r.data.booked_count} month(s) through ${r.data.through_month}`);
      } else {
        toast.info("Already caught up — nothing to book");
      }
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Catch-up failed");
    } finally {
      setCatchingUp(false);
    }
  };

  const bookNow = async () => {
    setBooking(true);
    try {
      const r = await api.post(`/depreciation/${schedule.id}/book`);
      toast.success(`Booked ${fmtMoney(r.data.amount)} depreciation for ${r.data.month}`);
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
    } finally {
      setBooking(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/depreciation/${schedule.id}`);
      toast.success("Depreciation schedule removed");
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const exportSchedule = async (format) => {
    try {
      await downloadFile(`/depreciation/${schedule.id}/export?format=${format}`,
        `depreciation_${(asset.name || "asset").replace(/\s+/g, "_")}.${format}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    }
  };

  if (!schedule) {
    const lifeNum = parseInt(life, 10) || 60;
    const salvageNum = parseFloat(salvage) || 0;
    const cost = asset.amount || 0;
    const depreciable = Math.max(cost - salvageNum, 0);
    // Mirror the backend's per-method amount list so the preview matches
    // what will be posted.
    const amounts = (() => {
      if (lifeNum <= 0) return [];
      if (method === "double_declining") {
        const rate = 2 / lifeNum;
        let book = cost;
        const out = [];
        for (let i = 0; i < lifeNum; i++) {
          let amt;
          if (i === lifeNum - 1) {
            amt = Math.max(book - salvageNum, 0);
          } else {
            amt = book * rate;
            if (book - amt < salvageNum) amt = Math.max(book - salvageNum, 0);
          }
          out.push(Math.round(amt * 100) / 100);
          book -= amt;
        }
        return out;
      }
      if (method === "sum_of_years_digits") {
        const total = (lifeNum * (lifeNum + 1)) / 2;
        return Array.from({ length: lifeNum }, (_, i) => Math.round((depreciable * (lifeNum - i)) / total * 100) / 100);
      }
      // straight-line
      const m = depreciable / lifeNum;
      return Array.from({ length: lifeNum }, () => Math.round(m * 100) / 100);
    })();
    const totalDeprec = amounts.reduce((a, b) => a + b, 0);
    const previewRows = amounts.map((monthlyAmt, i) => {
      const d = new Date();
      d.setMonth(d.getMonth() + i);
      const label = d.toLocaleString("en-US", { month: "short", year: "numeric" });
      let book = cost;
      for (let j = 0; j <= i; j++) book -= amounts[j];
      return { idx: i + 1, label, monthly: monthlyAmt, bookValue: Math.max(book, salvageNum) };
    });
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`bs-depr-setup-${asset.id}`}
          title="Set up straight-line monthly depreciation"
          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border inline-flex items-center gap-1 text-[var(--ll-ink-soft)] hover:bg-[var(--ll-mint)]"
          style={{ borderColor: "var(--ll-border)" }}
        >
          <Clock size={9} /> Set up depreciation?
        </button>
        {open && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={() => setOpen(false)}>
            <div className="bg-white border max-w-2xl w-full p-5 max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} style={{ borderColor: "var(--ll-border)" }} data-testid="bs-depr-modal">
              <div className="font-display font-bold text-xl mb-1">Depreciation — {asset.name}</div>
              <div className="text-xs text-[var(--ll-ink-soft)] mb-4">Straight-line, posted as a monthly Depreciation expense when you tap "Book this month".</div>

              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Method</label>
                  <select
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                    data-testid="bs-depr-method"
                    className="w-full px-2 py-2 border text-sm bg-white"
                    style={{ borderColor: "var(--ll-border)" }}
                  >
                    <option value="straight_line">Straight-line</option>
                    <option value="double_declining">Double declining</option>
                    <option value="sum_of_years_digits">Sum-of-years digits</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Useful life (months)</label>
                  <input type="number" min={1} value={life} onChange={(e) => setLife(e.target.value)} data-testid="bs-depr-life" className="w-full px-2 py-2 border text-sm font-mono-fin" style={{ borderColor: "var(--ll-border)" }} />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Salvage value</label>
                  <input type="number" step="0.01" value={salvage} onChange={(e) => setSalvage(e.target.value)} data-testid="bs-depr-salvage" className="w-full px-2 py-2 border text-sm font-mono-fin" style={{ borderColor: "var(--ll-border)" }} />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs mb-3 p-3" style={{ background: "#F8FAFC" }} data-testid="bs-depr-summary">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Monthly (first)</div>
                  <div className="font-mono-fin text-sm">{fmtMoney(amounts[0] || 0)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">End date</div>
                  <div className="font-mono-fin text-sm">{previewRows.length ? previewRows[previewRows.length - 1].label : "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Total depreciation</div>
                  <div className="font-mono-fin text-sm">{fmtMoney(totalDeprec)}</div>
                </div>
              </div>

              <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Schedule preview</div>
              <div className="flex-1 overflow-y-auto border mb-4" style={{ borderColor: "var(--ll-border)" }} data-testid="bs-depr-preview">
                <table className="w-full text-xs">
                  <thead className="sticky top-0" style={{ background: "#0F1E3C", color: "white" }}>
                    <tr>
                      <th className="px-3 py-1.5 text-left font-medium">#</th>
                      <th className="px-3 py-1.5 text-left font-medium">Month</th>
                      <th className="px-3 py-1.5 text-right font-medium">Monthly</th>
                      <th className="px-3 py-1.5 text-right font-medium">Book value after</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r) => (
                      <tr key={r.idx} className="border-t" style={{ borderColor: "var(--ll-border)" }}>
                        <td className="px-3 py-1 font-mono-fin text-[var(--ll-ink-muted)]">{r.idx}</td>
                        <td className="px-3 py-1">{r.label}</td>
                        <td className="px-3 py-1 text-right font-mono-fin">{fmtMoney(r.monthly)}</td>
                        <td className="px-3 py-1 text-right font-mono-fin">{fmtMoney(r.bookValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-end gap-2">
                <button type="button" onClick={() => setOpen(false)} className="px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}>Cancel</button>
                <button type="button" onClick={setup} disabled={saving} data-testid="bs-depr-save" className="px-4 py-2 bg-[#0b5d9e] text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Set up
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="inline-flex items-center gap-1">
      <span
        className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 inline-flex items-center gap-1"
        style={{ background: "var(--ll-mint)", color: "var(--ll-ink)" }}
        title={`${schedule.useful_life_months} mo · ${(schedule.method || "straight_line").replace(/_/g, " ")} · ${totalBooked}/${totalLife} booked`}
        data-testid={`bs-depr-badge-${asset.id}`}
      >
        <Clock size={9} /> {fmtMoney(monthly)}/mo
      </span>
      {!bookedThisMonth ? (
        <button
          type="button"
          onClick={bookNow}
          disabled={booking}
          data-testid={`bs-depr-book-${asset.id}`}
          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border inline-flex items-center gap-1 hover:bg-[var(--ll-mint)] disabled:opacity-50"
          style={{ borderColor: "var(--ll-border)" }}
        >
          {booking ? <Loader2 size={9} className="animate-spin" /> : <Plus size={9} />} Book this month
        </button>
      ) : (
        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 inline-flex items-center gap-1" style={{ background: "#D1FAE5", color: "#065F46" }}>
          <Check size={9} /> Booked {thisMonthIso}
        </span>
      )}
      {canCatchUp && (
        <button
          type="button"
          onClick={catchUp}
          disabled={catchingUp}
          data-testid={`bs-depr-catchup-${asset.id}`}
          title={`Book every unbooked month from the schedule start through today (${totalBooked}/${totalLife} booked so far)`}
          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border inline-flex items-center gap-1 hover:bg-[var(--ll-mint)] disabled:opacity-50"
          style={{ borderColor: "var(--ll-border)" }}
        >
          {catchingUp ? <Loader2 size={9} className="animate-spin" /> : <Clock size={9} />} Catch up
        </button>
      )}
      <button
        type="button"
        onClick={() => exportSchedule("pdf")}
        aria-label="Export depreciation schedule as PDF"
        title="Export full schedule as PDF"
        data-testid={`bs-depr-export-pdf-${asset.id}`}
        className="p-0.5 text-[var(--ll-ink-soft)] hover:text-[#0b5d9e]"
      >
        <FileText size={11} />
      </button>
      <button
        type="button"
        onClick={() => exportSchedule("xlsx")}
        aria-label="Export depreciation schedule as Excel"
        title="Export full schedule as Excel"
        data-testid={`bs-depr-export-xlsx-${asset.id}`}
        className="p-0.5 text-[var(--ll-ink-soft)] hover:text-[#0b5d9e]"
      >
        <FileSpreadsheet size={11} />
      </button>
      <button
        type="button"
        onClick={remove}
        aria-label="Remove depreciation schedule"
        className="p-0.5 text-[var(--ll-ink-soft)] hover:text-red-600"
      >
        <Trash2 size={10} />
      </button>
    </div>
  );
}
